// Settle sheet — slides up *inside* the drawer.
// One amount + a method dropdown. Form morphs by method. Maps to the four
// BE write endpoints from the audit:
//   card_on_file / invoice → /payment-requests
//   remind_invoice         → /invoices/{id}/remind
//   void_invoice           → /invoices/{id}/reconcile
//   cash / external        → /payments (selected lines paid, or external creates charge)
//   write_off              → /write-offs

import { useEffect, useMemo, useState } from 'react';
import {
  type DisplayEncounterChargeGroup,
} from '@features/billing/components/charge-groups';
import { useRecordPayment, useWriteOff } from '@features/billing/store/hooks';
import { extractApiErrorMessage } from '@features/billing-readiness/utils/errorUtils';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import {
  usePatientPaymentMethods,
  useReconcileInvoice,
  useSendInvoiceReminder,
  useSendPaymentRequest,
} from '@features/payments/store/hooks';
import { usePatientById } from '@hooks/usePatientById';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Slide,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { getPatientPrimaryContactPhone } from '@utils/patientUtils';
import { useSnackbar } from 'notistack';

import { useRecentEncounterOptions } from '../hooks/useRecentEncounterOptions';
import type { SettleMethod, SettlePaymentIntent } from '../types';
import {
  buildCopayDeclarationTargets,
  buildPaymentAllocationTargets,
  buildRecordPaymentRequests,
  computeAllocation,
  type CopaySuggestion,
  type RecordPaymentAllocationRequest,
} from '../utils/allocationEngine';

import { SettleAllocationSection } from './SettleSheetAllocation';
import {
  SettleCopayDeclarationSection,
  SettlePaymentIntentControl,
} from './SettleSheetCopayDeclaration';

interface Props {
  patientId: string;
  patientName?: string | null;
  encounterId?: string | null;
  initialMethod?: SettleMethod;
  /**
   * Preset for the "What is this payment?" control (payment methods only).
   * The toolbar's copay route passes 'copay'; defaults to 'general'.
   */
  initialPaymentIntent?: SettlePaymentIntent;
  initialAmountCents: number;
  /**
   * BE-synthesized default for the "Reason (shown on invoice)" field, from
   * /preview's `suggested_reason`. Used as the prefill for invoice + card
   * methods. User can edit before sending.
   */
  suggestedReason?: string;
  /** Exact charge lines safe to place on a newly-created invoice. */
  invoiceChargeLineIds?: string[];
  /** Exact charge lines safe to write off. Includes invoiced lines. */
  writableChargeLineIds?: string[];
  /** Sum of invoiceable receipt lines represented by invoiceChargeLineIds. */
  invoiceableAmountCents?: number;
  /**
   * Unapplied account credit from /preview's `unallocated_credit_cents`.
   * Invoice/card methods net it against the selected lines so the patient
   * is never asked to re-pay money already on their account (gdn-2512).
   */
  unallocatedCreditCents?: number;
  /** Preview-derived encounter/charge-line groups for settlement selection. */
  chargeGroups?: DisplayEncounterChargeGroup[];
  /** First open-invoice id on this patient — required for `remind_invoice`. */
  reminderInvoiceId?: string;
  /**
   * Whether invoice/card methods start with all uncovered lines selected.
   * Settle-balance entries keep the default (true); the toolbar Charge
   * composer passes false so the user opts lines *into* the new invoice.
   */
  preselectChargeLines?: boolean;
  /**
   * Eligibility-suggested copay for the drawer's scope (amount + provenance),
   * threaded from the drawer's eligibility snapshot. Drives the fill-the-
   * amount chip and the synthetic copay allocation target (settle sheet V2).
   */
  copaySuggestion?: CopaySuggestion | null;
  source?: string;
  onClose: () => void;
}

interface MethodOption {
  value: SettleMethod;
  label: string;
  sub: string;
  /** When true, hide from the dropdown for the current scope. */
  unavailable?: boolean;
}

/** Stable empty set — the copay declaration ignores waterfall exclusions. */
const EMPTY_EXCLUSIONS: ReadonlySet<string> = new Set<string>();

export const SettleSheet = ({
  patientId,
  patientName,
  encounterId,
  initialMethod,
  initialPaymentIntent,
  initialAmountCents,
  suggestedReason,
  invoiceChargeLineIds,
  writableChargeLineIds,
  invoiceableAmountCents,
  unallocatedCreditCents = 0,
  chargeGroups = [],
  reminderInvoiceId,
  preselectChargeLines = true,
  copaySuggestion,
  source,
  onClose,
}: Props) => {
  const isEncounterScope = !!encounterId;
  const { enqueueSnackbar } = useSnackbar();
  const { payment } = useEventTracking();

  const [method, setMethod] = useState<SettleMethod>(initialMethod ?? 'cash');
  // Whether the user has explicitly picked a method. Until then, the sheet
  // may re-resolve the method when async data (cards, preview) lands —
  // afterwards the user's choice is never overridden.
  const [methodTouched, setMethodTouched] = useState(false);
  // "What is this payment?" — copay pins the payment to a declared visit
  // (GDN-2405). Only meaningful for the payment methods (cash/external).
  const [paymentIntent, setPaymentIntent] = useState<SettlePaymentIntent>(
    initialPaymentIntent ?? 'general',
  );
  const [declaredVisitId, setDeclaredVisitId] = useState<string | null>(
    encounterId ?? null,
  );
  // Invoice-like methods with preselected receipt lines must NOT seed the
  // NEW-charge field from the caller's amount: callers pass the patient's
  // balance, and the preselected lines already carry that balance — seeding
  // both doubles the invoice AND mints a duplicate ad-hoc charge on the
  // ledger. Prefill is deferred to the effect below, which only fills once
  // the preview proves there are no invoiceable lines (pure ad-hoc invoice).
  const [amountStr, setAmountStr] = useState<string>(() => {
    const initialIsInvoiceLike =
      initialMethod === 'invoice' || initialMethod === 'card_on_file';
    if (initialIsInvoiceLike && preselectChargeLines) return '';
    return initialAmountCents > 0 ? (initialAmountCents / 100).toFixed(2) : '';
  });
  const [amountTouched, setAmountTouched] = useState(false);
  // Method-specific form state — kept narrow; we only persist what's submitted.
  const [paymentMethodKind, setPaymentMethodKind] = useState<'cash' | 'check'>('cash');
  const [externalKind, setExternalKind] = useState<'square' | 'venmo' | 'other_pos'>('square');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [collectedAt, setCollectedAt] = useState('');
  const [note, setNote] = useState('');
  const [invoiceReason, setInvoiceReason] = useState(suggestedReason ?? '');
  const [invoiceReasonTouched, setInvoiceReasonTouched] = useState(false);
  const [mobileOverride, setMobileOverride] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [writeOffReason, setWriteOffReason] =
    useState<'hardship' | 'bad_debt' | 'courtesy' | 'other'>('courtesy');
  const [selectedChargeLineIds, setSelectedChargeLineIds] = useState<string[]>([]);
  // V2 allocation preview: targets the user tapped OUT of the waterfall.
  const [excludedTargetIds, setExcludedTargetIds] = useState<Set<string>>(
    () => new Set(),
  );

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [cardOnFileId, setCardOnFileId] = useState<string>('');

  // Patient primary contact phone — fetched async, used to prefill the invoice's
  // mobile-override field. Same await-and-fill pattern as suggestedReason.
  const { patient } = usePatientById(patientId);
  const patientPrimaryContactPhone = getPatientPrimaryContactPhone(patient);

  // Saved cards on file. Drives:
  //   - whether the card_on_file method is offered at all
  //   - the card picker shown when card_on_file is selected
  //   - which Stripe payment_method_id we pass on submit
  const {
    paymentMethods,
    isStripeEnabled,
    isLoading: isPaymentMethodsLoading,
  } = usePatientPaymentMethods(patientId);
  const savedCards = useMemo(
    () => paymentMethods.filter((pm) => pm.type === 'card' || pm.card_brand),
    [paymentMethods],
  );
  const defaultCard = savedCards.find((pm) => pm.is_default) ?? savedCards[0];

  useEffect(() => {
    if (!cardOnFileId && defaultCard?.id) setCardOnFileId(defaultCard.id);
  }, [cardOnFileId, defaultCard?.id]);

  // /preview returns asynchronously, so the suggestedReason may not be
  // available at mount. Fill the reason field in once it arrives, but only
  // if the user hasn't typed in it yet.
  useEffect(() => {
    if (suggestedReason && !invoiceReason && !invoiceReasonTouched) {
      setInvoiceReason(suggestedReason);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedReason, invoiceReasonTouched]);

  useEffect(() => {
    if (patientPrimaryContactPhone && !mobileOverride) {
      setMobileOverride(patientPrimaryContactPhone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientPrimaryContactPhone]);

  const { recordPayment } = useRecordPayment();
  const { writeOff } = useWriteOff();
  const { mutate: sendPaymentRequest } = useSendPaymentRequest();
  const { sendReminder } = useSendInvoiceReminder();
  const { reconcileInvoice } = useReconcileInvoice();

  // Visit options for the copay declaration — context encounter first,
  // recent visits behind it. Only fetched for the payment methods, which
  // are the only place the "What is this payment?" control renders.
  const { visits: copayVisits, isLoading: isCopayVisitsLoading } =
    useRecentEncounterOptions({
      patientId,
      contextEncounterId: encounterId,
      skip: method !== 'cash' && method !== 'external',
    });

  // Default the declared visit to the context encounter (already seeded in
  // state) or the most recent visit once the list lands.
  useEffect(() => {
    if (!declaredVisitId && copayVisits.length > 0) {
      setDeclaredVisitId(copayVisits[0].id);
    }
  }, [copayVisits, declaredVisitId]);

  const methodOptions: MethodOption[] = [
    {
      value: 'void_invoice',
      label: 'Void invoice',
      sub: reminderInvoiceId ? 'Release linked charge lines' : 'No open invoice',
      unavailable: !reminderInvoiceId,
    },
    {
      value: 'card_on_file',
      label: 'Charge card on file',
      sub: defaultCard
        ? formatCard(defaultCard)
        : isStripeEnabled
          ? 'No card on file'
          : 'Stripe Connect not set up',
      unavailable: !defaultCard,
    },
    {
      value: 'invoice',
      label: 'Send invoice',
      sub: 'Stripe link via SMS',
    },
    {
      value: 'remind_invoice',
      label: 'Resend invoice',
      sub: reminderInvoiceId ? 'Existing open invoice' : 'No open invoice',
      unavailable: !reminderInvoiceId,
    },
    { value: 'cash', label: 'Cash / check', sub: 'Recorded manually' },
    {
      value: 'external',
      label: 'External collection',
      sub: 'Square / POS / Venmo',
    },
    { value: 'write_off', label: 'Write off', sub: 'Adjustment, no money moves' },
  ];

  const cents = Math.round((Number(amountStr) || 0) * 100);
  const requiresAmount = method !== 'remind_invoice' && method !== 'void_invoice';
  const isInvoiceLikeMethod = method === 'invoice' || method === 'card_on_file';
  // The recordPayment methods (cash/external) are amount-first: the live
  // allocation preview replaces checkbox selection entirely (GDN-2400).
  // /payment-requests (invoice, card_on_file) has no partial-allocation or
  // credit-remainder semantics, so those keep the invoice-composer flow, and
  // write-offs keep the explicit line selection.
  const isPaymentMethod = method === 'cash' || method === 'external';
  const isChargeLineSelectionMethod =
    method === 'invoice' || method === 'card_on_file' || method === 'write_off';
  const selectableChargeLineIds = useMemo(() => {
    const ids =
      method === 'write_off' || method === 'cash' || method === 'external'
        ? writableChargeLineIds
        : invoiceChargeLineIds;
    return ids && ids.length > 0 ? ids : [];
  }, [invoiceChargeLineIds, method, writableChargeLineIds]);
  const selectableChargeLineIdSet = useMemo(
    () => new Set(selectableChargeLineIds),
    [selectableChargeLineIds],
  );
  const selectedChargeLineIdSet = useMemo(
    () => new Set(selectedChargeLineIds),
    [selectedChargeLineIds],
  );
  const selectedChargeLineAmountCents = useMemo(() => {
    let total = 0;
    for (const group of chargeGroups) {
      for (const line of group.lines) {
        if (selectedChargeLineIdSet.has(line.id)) total += line.amountCents;
      }
    }
    return total;
  }, [chargeGroups, selectedChargeLineIdSet]);
  const selectedInvoiceReason = useMemo(
    () => buildSelectedInvoiceReason(chargeGroups, selectedChargeLineIdSet),
    [chargeGroups, selectedChargeLineIdSet],
  );
  const selectedPaymentRequestChargeLineIds =
    isInvoiceLikeMethod && selectedChargeLineIds.length > 0
      ? selectedChargeLineIds
      : undefined;
  const selectedWriteOffChargeLineIds =
    method === 'write_off' && selectedChargeLineIds.length > 0
      ? selectedChargeLineIds
      : undefined;
  // Invoice composer math: the Amount field is the NEW ad-hoc charge; selected
  // receipt lines add on top. Falls back to the preview-level total when the
  // receipt groups haven't hydrated but lines are selected.
  const selectedLinesTotalCents = !isInvoiceLikeMethod
    ? 0
    : selectedChargeLineAmountCents > 0
      ? selectedChargeLineAmountCents
      : selectedChargeLineIds.length > 0
        ? invoiceableAmountCents ?? 0
        : 0;
  const newChargeCents = isInvoiceLikeMethod ? cents : 0;
  // Account credit nets against the selected lines (never the new ad-hoc
  // charge) and never below zero — mirrors the BE's total_due_cents clamp.
  // Only subtract when the total is the raw per-line sum: the hydration
  // fallback above is preview total_due_cents, which is already credit-
  // clamped — netting it again would undercharge by the credit amount.
  const creditAppliedCents =
    isInvoiceLikeMethod && selectedChargeLineAmountCents > 0
      ? Math.min(unallocatedCreditCents, selectedLinesTotalCents)
      : 0;
  const invoiceTotalCents =
    selectedLinesTotalCents - creditAppliedCents + newChargeCents;
  const writeOffAmountMismatch =
    method === 'write_off' &&
    !!selectedWriteOffChargeLineIds?.length &&
    selectedChargeLineAmountCents > 0 &&
    cents !== selectedChargeLineAmountCents;

  // ── Amount-first allocation (GDN-2400) + copay declaration (GDN-2405) ──
  const isCopayDeclared = isPaymentMethod && paymentIntent === 'copay';
  const allocationTargets = useMemo(() => {
    if (!isPaymentMethod) return [];
    if (isCopayDeclared) {
      // Declared copay: the selected visit collects first; over-amounts
      // waterfall into other visits' open lines, remainder → credit.
      return declaredVisitId
        ? buildCopayDeclarationTargets({
            chargeGroups,
            selectableChargeLineIds,
            declaredEncounterId: declaredVisitId,
            copaySuggestion,
            amountCents: cents,
          })
        : [];
    }
    return buildPaymentAllocationTargets({
      chargeGroups,
      selectableChargeLineIds,
      copaySuggestion,
      encounterId,
    });
  }, [
    cents,
    chargeGroups,
    copaySuggestion,
    declaredVisitId,
    encounterId,
    isCopayDeclared,
    isPaymentMethod,
    selectableChargeLineIds,
  ]);
  const allocation = useMemo(
    () =>
      computeAllocation(
        cents,
        allocationTargets,
        // Row exclusions belong to the general waterfall UI — the copay
        // declaration has no toggleable rows, so stale exclusions (shared
        // charge-line ids) must not leak across the intent switch.
        isCopayDeclared ? EMPTY_EXCLUSIONS : excludedTargetIds,
      ),
    [cents, allocationTargets, excludedTargetIds, isCopayDeclared],
  );

  const declaredVisit =
    copayVisits.find(visit => visit.id === declaredVisitId) ?? copayVisits[0];

  const canSubmit =
    !submitting &&
    (isPaymentMethod
      ? // Amount-first: any positive amount submits — allocation never
        // blocks. A declared copay additionally needs its visit.
        cents > 0 && (!isCopayDeclared || !!declaredVisitId)
      : (!requiresAmount || (isInvoiceLikeMethod ? invoiceTotalCents > 0 : cents > 0)) &&
        // Invoice-like methods don't require line selection — an unselected
        // receipt just means the invoice is purely the new ad-hoc charge.
        (isInvoiceLikeMethod ||
          !isChargeLineSelectionMethod ||
          selectedChargeLineIds.length > 0 ||
          selectableChargeLineIds.length === 0) &&
        ((method !== 'remind_invoice' && method !== 'void_invoice') ||
          !!reminderInvoiceId));

  useEffect(() => {
    // Payment methods replace checkbox selection with the allocation
    // preview — leave selectedChargeLineIds alone so switching to a
    // selection method (e.g. write_off) re-derives it cleanly.
    if (!isChargeLineSelectionMethod) return;
    if (isInvoiceLikeMethod && !preselectChargeLines) {
      setSelectedChargeLineIds([]);
      return;
    }
    setSelectedChargeLineIds(selectableChargeLineIds);
  }, [
    isChargeLineSelectionMethod,
    isInvoiceLikeMethod,
    preselectChargeLines,
    selectableChargeLineIds,
  ]);

  // Re-resolve the method when async data lands, but never override an
  // explicit user choice. This kills the mount race where the sheet showed
  // "Send invoice" while the dropdown no longer offered it.
  useEffect(() => {
    if (methodTouched) return;
    if (method === 'card_on_file' && !isPaymentMethodsLoading && !defaultCard) {
      setMethod('invoice');
    }
  }, [method, methodTouched, isPaymentMethodsLoading, defaultCard]);

  useEffect(() => {
    if (!isInvoiceLikeMethod || invoiceReasonTouched || !selectedInvoiceReason) return;
    setInvoiceReason(selectedInvoiceReason);
  }, [invoiceReasonTouched, isInvoiceLikeMethod, selectedInvoiceReason]);

  // Invoice-like methods with preselection suppress the caller's amount:
  // the preselected receipt lines already carry the balance the caller
  // passed, so writing it into the NEW-charge field would double-bill
  // (invoice = lines + a duplicate ad-hoc charge). `undefined` line ids
  // mean the preview hasn't resolved yet — stay suppressed until it proves
  // there is nothing to preselect. The opt-in composer (preselectChargeLines
  // false) keeps its explicit prefill: nothing is selected on its behalf.
  const suppressInvoicePrefill =
    preselectChargeLines &&
    (invoiceChargeLineIds === undefined || invoiceChargeLineIds.length > 0);

  useEffect(() => {
    if (amountTouched || !requiresAmount) return;
    // Invoice-like methods treat the field as the NEW charge — selected
    // receipt lines are added in the displayed total, never written back
    // into the field. Payment methods are amount-first: the allocation
    // follows the amount, never the other way around.
    const nextAmount = isInvoiceLikeMethod
      ? suppressInvoicePrefill
        ? 0
        : initialAmountCents
      : isPaymentMethod
        ? initialAmountCents
        : isChargeLineSelectionMethod && selectedChargeLineAmountCents > 0
          ? selectedChargeLineAmountCents
          : initialAmountCents;
    setAmountStr(nextAmount > 0 ? (nextAmount / 100).toFixed(2) : '');
  }, [
    amountTouched,
    initialAmountCents,
    isChargeLineSelectionMethod,
    isInvoiceLikeMethod,
    isPaymentMethod,
    requiresAmount,
    selectedChargeLineAmountCents,
    suppressInvoicePrefill,
  ]);

  // ── Allocation interactions ────────────────────────────────────────────
  const toggleAllocationTarget = (targetId: string, included: boolean) => {
    const target = allocationTargets.find(candidate => candidate.id === targetId);
    payment.settleAllocationToggled({
      target_kind: target?.kind,
      included,
      encounter_id: encounterId ?? undefined,
      source: source ?? 'ledger_drawer',
    });
    setExcludedTargetIds(current => {
      const next = new Set(current);
      if (included) next.delete(targetId);
      else next.add(targetId);
      return next;
    });
  };

  const handleCopayChipClick = () => {
    if (!copaySuggestion) return;
    payment.settleCopayChipClicked({
      amount_cents: copaySuggestion.amountCents,
      encounter_id: encounterId ?? undefined,
      source: source ?? 'ledger_drawer',
    });
    setAmountTouched(true);
    setAmountStr((copaySuggestion.amountCents / 100).toFixed(2));
  };

  const handlePaymentIntentChange = (next: SettlePaymentIntent) => {
    payment.settlePaymentIntentChanged({
      intent: next,
      encounter_id: encounterId ?? undefined,
      source: source ?? 'ledger_drawer',
    });
    setPaymentIntent(next);
  };

  const toggleChargeLine = (chargeLineId: string, selected: boolean) => {
    // For payment/write-off the Amount mirrors the selection, so re-sync it.
    // For invoice-like methods the field is the user's NEW charge — toggling
    // receipt lines must never wipe it.
    if (!isInvoiceLikeMethod) setAmountTouched(false);
    setSelectedChargeLineIds((current) => {
      if (selected) return Array.from(new Set([...current, chargeLineId]));
      return current.filter(id => id !== chargeLineId);
    });
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      if (writeOffAmountMismatch) {
        throw new Error(
          `Write-off amount must match the selected charge lines: ${dollars(selectedChargeLineAmountCents)}.`,
        );
      }
      switch (method) {
        case 'card_on_file': {
          if (!cardOnFileId) throw new Error('No card on file');
          const result = await sendPaymentRequest({
            patient_id: patientId,
            amount: (invoiceTotalCents / 100).toFixed(2),
            reason: invoiceReason || 'Payment',
            encounter_id: encounterId ?? undefined,
            charge_line_ids: selectedPaymentRequestChargeLineIds,
            new_charge:
              newChargeCents > 0
                ? {
                    amount_cents: newChargeCents,
                    reason: invoiceReason || 'Patient charges',
                  }
                : undefined,
            payment_method_id: cardOnFileId,
            send_notification: false,
            source: source ?? 'ledger_drawer',
          });
          if (!result) throw new Error('Failed to charge card on file');
          payment.requestSent({
            source: source ?? 'ledger_drawer',
            amount: invoiceTotalCents,
            delivery_method: 'card_on_file',
          });
          enqueueSnackbar(`Charged ${dollars(invoiceTotalCents)} to card on file`, {
            variant: 'success',
          });
          break;
        }
        case 'invoice': {
          const result = await sendPaymentRequest({
            patient_id: patientId,
            amount: (invoiceTotalCents / 100).toFixed(2),
            reason: invoiceReason || 'Payment',
            encounter_id: encounterId ?? undefined,
            charge_line_ids: selectedPaymentRequestChargeLineIds,
            new_charge:
              newChargeCents > 0
                ? {
                    amount_cents: newChargeCents,
                    reason: invoiceReason || 'Patient charges',
                  }
                : undefined,
            mobile_phone: mobileOverride || undefined,
            send_notification: sendNotification,
            source: source ?? 'ledger_drawer',
          });
          if (!result) throw new Error('Failed to send invoice');
          payment.requestSent({
            source: source ?? 'ledger_drawer',
            amount: invoiceTotalCents,
            delivery_method: sendNotification ? 'sms' : 'silent',
          });
          enqueueSnackbar(`Invoice for ${dollars(invoiceTotalCents)} sent`, {
            variant: 'success',
          });
          break;
        }
        case 'remind_invoice': {
          if (!reminderInvoiceId) throw new Error('No open invoice to remind');
          await sendReminder(reminderInvoiceId);
          enqueueSnackbar('Reminder sent', { variant: 'success' });
          break;
        }
        case 'void_invoice': {
          if (!reminderInvoiceId) throw new Error('No open invoice to void');
          const result = await reconcileInvoice(reminderInvoiceId, {
            action: 'void_invoice',
            sendNotification: false,
          });
          if (!result) throw new Error('Failed to void invoice');
          enqueueSnackbar('Invoice voided', { variant: 'success' });
          break;
        }
        case 'cash':
        case 'external': {
          const paymentMethodLabel =
            method === 'cash' ? paymentMethodKind : externalKind;
          // The allocation decides the request payloads — partial line
          // allocations, copay create_charge macro, or pure credit. A mixed
          // copay + charge-line allocation splits into one request per
          // portion (the BE can't express both in a single call). A declared
          // copay scopes the payload to the DECLARED visit, which may differ
          // from the drawer's context encounter.
          const paymentRequests: RecordPaymentAllocationRequest[] =
            buildRecordPaymentRequests(allocation, {
              isEncounterScope: isCopayDeclared ? true : isEncounterScope,
              encounterId: isCopayDeclared ? declaredVisitId : encounterId,
              method,
            });
          let recordedCents = 0;
          for (const [index, request] of paymentRequests.entries()) {
            const result = await recordPayment(patientId, {
              amount_cents: request.amount_cents,
              payment_method: paymentMethodLabel,
              encounter_id: request.encounter_id,
              charge_line_ids: request.charge_line_ids,
              // The reference number seeds the BE dedup key
              // (`manual:{reference_number}`) — suffix the later parts of a
              // split payment so they don't dedup-collide with the first.
              reference_number: !referenceNumber
                ? null
                : index === 0
                  ? referenceNumber
                  : `${referenceNumber}-${index + 1}`,
              note: note || null,
              collected_at: collectedAt ? new Date(collectedAt).toISOString() : null,
              create_charge: request.create_charge,
            });
            if (!result.success) {
              const message = extractApiErrorMessage(
                result.error,
                "Couldn't record the payment. Please try again.",
              );
              // A later part of a split payment failing means money already
              // landed — say how much, so the user doesn't re-enter it all.
              throw new Error(
                index === 0
                  ? message
                  : `Recorded ${dollars(recordedCents)}, but ${dollars(request.amount_cents)} of the payment failed: ${message}`,
              );
            }
            recordedCents += request.amount_cents;
          }
          payment.paymentRecorded({
            encounter_id: encounterId ?? undefined,
            payment_method: paymentMethodLabel,
            amount_cents: cents,
            allocated: paymentRequests.some(
              request =>
                !!(
                  request.charge_line_ids?.length ||
                  request.create_charge ||
                  request.encounter_id
                ),
            ),
          });
          if (allocation.creditCents > 0) {
            payment.settleCreditRemainderSubmitted({
              amount_cents: cents,
              credit_cents: allocation.creditCents,
              payment_method: paymentMethodLabel,
              encounter_id: encounterId ?? undefined,
              source: source ?? 'ledger_drawer',
            });
          }
          if (isCopayDeclared && declaredVisitId) {
            payment.settleCopayDeclaredSubmitted({
              amount_cents: cents,
              encounter_id: declaredVisitId,
              payment_method: paymentMethodLabel,
              used_suggestion:
                !!copaySuggestion && cents === copaySuggestion.amountCents,
              source: source ?? 'ledger_drawer',
            });
          }
          enqueueSnackbar(
            isCopayDeclared
              ? `Recorded ${dollars(cents)} co-pay`
              : method === 'cash'
                ? `Recorded ${dollars(cents)} ${paymentMethodLabel} payment`
                : `Logged ${dollars(cents)} external collection`,
            { variant: 'success' },
          );
          break;
        }
        case 'write_off': {
          const result = await writeOff(patientId, {
            amount_cents: cents,
            reason: writeOffReason,
            note: note || null,
            encounter_id: selectedWriteOffChargeLineIds ? null : encounterId ?? null,
            charge_line_ids: selectedWriteOffChargeLineIds,
          });
          if (!result.success) {
            throw new Error(
              extractApiErrorMessage(
                result.error,
                "Couldn't record the write-off. Please try again.",
              ),
            );
          }
          enqueueSnackbar(`Wrote off ${dollars(cents)}`, { variant: 'success' });
          break;
        }
      }
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          top: 'auto',
          bottom: 0,
          maxHeight: '85%',
          bgcolor: ELIG_TOKENS.card,
          borderTop: `1px solid ${ELIG_TOKENS.lineStrong}`,
          boxShadow: '0 -8px 24px rgba(28,30,133,0.10)',
          display: 'flex',
          flexDirection: 'column',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        <SheetHeader
          title={`${isInvoiceLikeMethod ? 'Charge' : 'Settle'} ${patientName ?? ''}`.trim()}
          subtitle={isEncounterScope ? 'Encounter scope' : 'Patient scope'}
          onClose={onClose}
        />

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
          {isInvoiceLikeMethod && reminderInvoiceId && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <>
                  <Button
                    size="small"
                    onClick={() => {
                      setMethodTouched(true);
                      setMethod('remind_invoice');
                    }}
                  >
                    Resend
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setMethodTouched(true);
                      setMethod('void_invoice');
                    }}
                  >
                    Void
                  </Button>
                </>
              }
            >
              This patient already has an open invoice. A new charge creates a
              second invoice and payment link.
            </Alert>
          )}

          {isPaymentMethod ? (
            // Amount-first flow: declare what the payment IS, then the
            // amount leads. Co-pay pins the money to a visit; General keeps
            // the live allocation waterfall. Never blocks — remainder →
            // account credit.
            <>
              <SettlePaymentIntentControl
                value={paymentIntent}
                onChange={handlePaymentIntentChange}
                copayDisabled={copayVisits.length === 0 && !isCopayVisitsLoading}
              />
              {isCopayDeclared ? (
                <SettleCopayDeclarationSection
                  amountStr={amountStr}
                  onAmountChange={(value) => {
                    setAmountTouched(true);
                    setAmountStr(value);
                  }}
                  amountCents={cents}
                  copaySuggestion={copaySuggestion}
                  onCopayChipClick={handleCopayChipClick}
                  visits={copayVisits}
                  selectedVisitId={declaredVisitId}
                  onVisitChange={setDeclaredVisitId}
                  isVisitsLoading={isCopayVisitsLoading}
                />
              ) : (
                <SettleAllocationSection
                  amountStr={amountStr}
                  onAmountChange={(value) => {
                    setAmountTouched(true);
                    setAmountStr(value);
                  }}
                  allocation={allocation}
                  copaySuggestion={copaySuggestion}
                  onCopayChipClick={handleCopayChipClick}
                  onToggleTarget={toggleAllocationTarget}
                />
              )}
            </>
          ) : (
            <>
              {isChargeLineSelectionMethod && chargeGroups.length > 0 && (
                <SettlementPreviewPanel
                  groups={chargeGroups}
                  method={method}
                  selectableIds={selectableChargeLineIdSet}
                  selectedIds={selectedChargeLineIdSet}
                  totalCents={selectedChargeLineAmountCents}
                  creditAppliedCents={creditAppliedCents}
                  onToggleLine={toggleChargeLine}
                />
              )}

              {requiresAmount && (
                <TextField
                  label={isInvoiceLikeMethod ? 'New charge' : 'Amount'}
                  value={amountStr}
                  onChange={(e) => {
                    setAmountTouched(true);
                    setAmountStr(e.target.value);
                  }}
                  fullWidth
                  size="small"
                  autoFocus
                  helperText={
                    isInvoiceLikeMethod
                      ? 'Creates a new ad-hoc charge on the ledger'
                      : undefined
                  }
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    },
                  }}
                  sx={{ mb: isInvoiceLikeMethod ? 1 : 2 }}
                />
              )}
            </>
          )}

          {isInvoiceLikeMethod && (
            <Typography
              variant="body2"
              sx={{ mb: 2, color: ELIG_TOKENS.midnight, fontWeight: 600 }}
            >
              {`Invoice total ${dollars(invoiceTotalCents)}`}
              {selectedLinesTotalCents > 0 &&
                (newChargeCents > 0 || creditAppliedCents > 0) && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: ELIG_TOKENS.inkFaint, ml: 1 }}
                  >
                    {`(${dollars(selectedLinesTotalCents)} selected charges${
                      creditAppliedCents > 0
                        ? ` − ${dollars(creditAppliedCents)} account credit`
                        : ''
                    }${newChargeCents > 0 ? ` + ${dollars(newChargeCents)} new` : ''})`}
                  </Typography>
                )}
              {creditAppliedCents > 0 && invoiceTotalCents === 0 && (
                <Typography
                  variant="caption"
                  sx={{ color: ELIG_TOKENS.inkSoft, display: 'block', mt: 0.5 }}
                >
                  Account credit covers the selection — nothing to charge.
                </Typography>
              )}
            </Typography>
          )}

          <Typography
            variant="overline"
            sx={{ color: ELIG_TOKENS.midnight, fontWeight: 600, display: 'block', mb: 1 }}
          >
            How?
          </Typography>
          <TextField
            select
            value={method}
            onChange={(e) => {
              setMethodTouched(true);
              setMethod(e.target.value as SettleMethod);
            }}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          >
            {methodOptions
              .filter((opt) => !opt.unavailable)
              .map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box>
                    <Typography variant="body2">{opt.label}</Typography>
                    <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
                      {opt.sub}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
          </TextField>

          <MethodFields
            method={method}
            paymentMethodKind={paymentMethodKind}
            setPaymentMethodKind={setPaymentMethodKind}
            externalKind={externalKind}
            setExternalKind={setExternalKind}
            referenceNumber={referenceNumber}
            setReferenceNumber={setReferenceNumber}
            collectedAt={collectedAt}
            setCollectedAt={setCollectedAt}
            note={note}
            setNote={setNote}
            invoiceReason={invoiceReason}
            setInvoiceReason={(next) => {
              setInvoiceReasonTouched(true);
              setInvoiceReason(next);
            }}
            mobileOverride={mobileOverride}
            setMobileOverride={setMobileOverride}
            sendNotification={sendNotification}
            setSendNotification={setSendNotification}
            writeOffReason={writeOffReason}
            setWriteOffReason={setWriteOffReason}
            savedCards={savedCards}
            cardOnFileId={cardOnFileId}
            setCardOnFileId={setCardOnFileId}
          />

        </Box>

        <Box
          sx={{
            borderTop: `1px solid ${ELIG_TOKENS.line}`,
            bgcolor: ELIG_TOKENS.bg,
            px: 3,
            py: 1.5,
          }}
        >
          {/* Pinned above the actions so a failed submit is visible even when
              the scrollable content (e.g. the V2 allocation waterfall) has
              pushed the sheet body below the fold. */}
          {submitError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {submitError}
            </Alert>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {isPaymentMethod && cents > 0 && (
              <Typography
                variant="caption"
                sx={{ color: ELIG_TOKENS.inkFaint, mr: 'auto' }}
              >
                {allocation.creditCents > 0
                  ? `${dollars(allocation.creditCents)} stays as account credit`
                  : isCopayDeclared && declaredVisit
                    ? `Linked to ${
                        declaredVisit.serviceDate
                          ? `${shortDate(declaredVisit.serviceDate)} visit`
                          : 'this visit'
                      }`
                    : 'Fully allocated'}
              </Typography>
            )}
            <Button onClick={onClose} sx={{ color: ELIG_TOKENS.inkSoft, textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit}
              sx={{
                bgcolor: ELIG_TOKENS.midnight,
                color: '#fff',
                '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.9)' },
                minWidth: 140,
              }}
            >
              {submitting ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : isCopayDeclared ? (
                cents > 0 ? `Record ${dollars(cents)} co-pay` : 'Record co-pay'
              ) : (
                primaryFor(method)
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Slide>
  );
};

const SettlementPreviewPanel = ({
  groups,
  method,
  selectableIds,
  selectedIds,
  totalCents,
  creditAppliedCents = 0,
  onToggleLine,
}: {
  groups: DisplayEncounterChargeGroup[];
  method: SettleMethod;
  selectableIds: Set<string>;
  selectedIds: Set<string>;
  totalCents: number;
  /** Account credit netted against the selection (invoice/card only). */
  creditAppliedCents?: number;
  onToggleLine: (chargeLineId: string, selected: boolean) => void;
}) => {
  const title =
    method === 'write_off' ? 'Write-off preview' : 'Invoice preview';
  const subtitle =
    method === 'write_off'
      ? 'What will be marked written off'
      : 'What will appear on this invoice';
  const rows = flattenPreviewRows(groups);
  return (
    <Box
      sx={{
        mb: 2,
        border: `1px solid ${ELIG_TOKENS.lineStrong}`,
        borderRadius: 1,
        bgcolor: ELIG_TOKENS.card,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: ELIG_TOKENS.bg,
          borderBottom: `1px solid ${ELIG_TOKENS.lineStrong}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{
              color: ELIG_TOKENS.midnight,
              display: 'block',
              fontWeight: 700,
              letterSpacing: 0.4,
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkSoft }}>
            {subtitle}
          </Typography>
        </Box>
        <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
          <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint, display: 'block' }}>
            Selected
          </Typography>
          <Typography variant="h6" sx={{ color: ELIG_TOKENS.ink, fontWeight: 700, lineHeight: 1.1 }}>
            {dollars(totalCents)}
          </Typography>
        </Box>
      </Box>

      <Box>
        {rows.map((row) => {
          const selectable = selectableIds.has(row.line.id);
          const selected = selectedIds.has(row.line.id);
          const lineLabel = receiptLineLabel(row.line);
          return (
            <Box
              key={row.line.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                gap: 1.25,
                alignItems: 'center',
                px: 2,
                py: 1.25,
                borderTop: `1px solid ${ELIG_TOKENS.line}`,
                bgcolor: selected ? ELIG_TOKENS.card : ELIG_TOKENS.bg,
                opacity: selectable ? 1 : 0.52,
              }}
            >
              <Checkbox
                checked={selected}
                disabled={!selectable}
                size="small"
                onChange={(event) => onToggleLine(row.line.id, event.target.checked)}
                slotProps={{ input: { 'aria-label': `Select ${lineLabel}` } }}
                sx={{ p: 0 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: ELIG_TOKENS.ink,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {lineLabel}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: ELIG_TOKENS.inkFaint,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {receiptLineSubtext(row)}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ color: ELIG_TOKENS.ink, fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                {dollars(row.line.amountCents)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {creditAppliedCents > 0 && (
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderTop: `1px solid ${ELIG_TOKENS.line}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: ELIG_TOKENS.midnight, fontWeight: 700 }}
          >
            Account credit applied
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: ELIG_TOKENS.green, fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            {dollars(-creditAppliedCents)}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: ELIG_TOKENS.amberTint,
          borderTop: `1px solid ${ELIG_TOKENS.lineStrong}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 700 }}>
          {creditAppliedCents > 0 ? 'Total after credit' : 'Selected total'}
        </Typography>
        <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 700 }}>
          {dollars(totalCents - creditAppliedCents)}
        </Typography>
      </Box>
    </Box>
  );
};

type SettlementPreviewRow = {
  group: DisplayEncounterChargeGroup;
  line: DisplayEncounterChargeGroup['lines'][number];
};

const flattenPreviewRows = (
  groups: DisplayEncounterChargeGroup[],
): SettlementPreviewRow[] =>
  groups.flatMap(group => group.lines.map(line => ({ group, line })));

const receiptLineLabel = (
  line: DisplayEncounterChargeGroup['lines'][number],
): string => {
  const code = normalizeCode(line.code);
  return (
    line.responsibilityLabel ??
    normalizeDescription(line.description, code) ??
    (code ? `CPT ${code}` : 'Charge')
  );
};

const receiptLineSubtext = ({ group, line }: SettlementPreviewRow): string => {
  const code = normalizeCode(line.code);
  const pieces = [
    group.serviceDate ? shortDate(group.serviceDate) : null,
    group.encounterTypeLabel,
    code && receiptLineLabel(line) !== `CPT ${code}` ? `CPT ${code}` : null,
    line.appliedPaymentCents && line.appliedPaymentCents > 0
      ? `paid ${dollars(line.appliedPaymentCents)}`
      : null,
  ].filter(Boolean);
  return pieces.join(' · ') || ' ';
};

// ─── Per-method morphing fields ──────────────────────────────────────────────

interface MethodFieldsProps {
  method: SettleMethod;
  paymentMethodKind: 'cash' | 'check';
  setPaymentMethodKind: (v: 'cash' | 'check') => void;
  externalKind: 'square' | 'venmo' | 'other_pos';
  setExternalKind: (v: 'square' | 'venmo' | 'other_pos') => void;
  referenceNumber: string;
  setReferenceNumber: (v: string) => void;
  collectedAt: string;
  setCollectedAt: (v: string) => void;
  note: string;
  setNote: (v: string) => void;
  invoiceReason: string;
  setInvoiceReason: (v: string) => void;
  mobileOverride: string;
  setMobileOverride: (v: string) => void;
  sendNotification: boolean;
  setSendNotification: (v: boolean) => void;
  writeOffReason: 'hardship' | 'bad_debt' | 'courtesy' | 'other';
  setWriteOffReason: (v: 'hardship' | 'bad_debt' | 'courtesy' | 'other') => void;
  savedCards: Array<{
    id: string;
    card_brand?: string | null;
    card_last4?: string | null;
    card_exp_month?: number | null;
    card_exp_year?: number | null;
    is_default?: boolean;
  }>;
  cardOnFileId: string;
  setCardOnFileId: (v: string) => void;
}

const MethodFields = (props: MethodFieldsProps) => {
  switch (props.method) {
    case 'card_on_file':
      return (
        <>
          {props.savedCards.length > 1 ? (
            <Field>
              <TextField
                select
                label="Card"
                value={props.cardOnFileId}
                onChange={(e) => props.setCardOnFileId(e.target.value)}
                fullWidth
                size="small"
              >
                {props.savedCards.map((card) => (
                  <MenuItem key={card.id} value={card.id}>
                    {formatCard(card)}
                  </MenuItem>
                ))}
              </TextField>
            </Field>
          ) : (
            <Helper>
              Will charge: <strong>{formatCard(props.savedCards[0])}</strong>. No SMS is sent.
            </Helper>
          )}
          <Field>
            <TextField
              label="Reason (shown on receipt)"
              value={props.invoiceReason}
              onChange={(e) => props.setInvoiceReason(e.target.value)}
              fullWidth
              size="small"
              placeholder="Office visit copay"
            />
          </Field>
        </>
      );
    case 'invoice':
      return (
        <>
          <Field>
            <TextField
              label="Mobile (optional override)"
              value={props.mobileOverride}
              onChange={(e) => props.setMobileOverride(e.target.value)}
              fullWidth
              size="small"
              placeholder="Uses patient primary contact phone"
            />
          </Field>
          <Field>
            <TextField
              label="Reason (shown on invoice)"
              value={props.invoiceReason}
              onChange={(e) => props.setInvoiceReason(e.target.value)}
              fullWidth
              size="small"
              placeholder="Office visit copay"
            />
          </Field>
          <Field>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={props.sendNotification}
                  onChange={(e) => props.setSendNotification(e.target.checked)}
                />
              }
              label="Text the patient when sent"
            />
          </Field>
        </>
      );
    case 'remind_invoice':
      return (
        <Helper>Re-sends the existing Stripe invoice. No new invoice created.</Helper>
      );
    case 'void_invoice':
      return (
        <Helper>
          Voids the open invoice and frees its receipt lines. No replacement invoice is
          created.
        </Helper>
      );
    case 'cash':
      return (
        <>
          <Field>
            <TextField
              select
              label="Method"
              value={props.paymentMethodKind}
              onChange={(e) => props.setPaymentMethodKind(e.target.value as 'cash' | 'check')}
              fullWidth
              size="small"
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="check">Check</MenuItem>
            </TextField>
          </Field>
          <Field>
            <TextField
              label="Reference # (optional)"
              value={props.referenceNumber}
              onChange={(e) => props.setReferenceNumber(e.target.value)}
              fullWidth
              size="small"
              placeholder="Receipt or check #"
            />
          </Field>
          <Field>
            <TextField
              label="Note"
              value={props.note}
              onChange={(e) => props.setNote(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
          </Field>
        </>
      );
    case 'external':
      return (
        <>
          <Field>
            <TextField
              select
              label="Source"
              value={props.externalKind}
              onChange={(e) =>
                props.setExternalKind(e.target.value as 'square' | 'venmo' | 'other_pos')
              }
              fullWidth
              size="small"
            >
              <MenuItem value="square">Square</MenuItem>
              <MenuItem value="venmo">Venmo</MenuItem>
              <MenuItem value="other_pos">Other POS</MenuItem>
            </TextField>
          </Field>
          <Field>
            <TextField
              label="Reference # (optional)"
              value={props.referenceNumber}
              onChange={(e) => props.setReferenceNumber(e.target.value)}
              fullWidth
              size="small"
              placeholder="Transaction id"
            />
          </Field>
          <Field>
            <TextField
              label="Collected at"
              type="datetime-local"
              value={props.collectedAt}
              onChange={(e) => props.setCollectedAt(e.target.value)}
              fullWidth
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Field>
        </>
      );
    case 'write_off':
      return (
        <>
          <Field>
            <TextField
              select
              label="Reason"
              value={props.writeOffReason}
              onChange={(e) =>
                props.setWriteOffReason(
                  e.target.value as 'hardship' | 'bad_debt' | 'courtesy' | 'other',
                )
              }
              fullWidth
              size="small"
            >
              <MenuItem value="hardship">Hardship</MenuItem>
              <MenuItem value="bad_debt">Bad debt</MenuItem>
              <MenuItem value="courtesy">Courtesy</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          </Field>
          <Field>
            <TextField
              label="Note"
              value={props.note}
              onChange={(e) => props.setNote(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
          </Field>
        </>
      );
  }
};

// ─── helpers ────────────────────────────────────────────────────────────────

const SheetHeader = ({
  title,
  subtitle,
  onClose,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
}) => (
  <Box
    sx={{
      px: 3,
      py: 1.5,
      borderBottom: `1px solid ${ELIG_TOKENS.line}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ELIG_TOKENS.midnight }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    <Button onClick={onClose} size="small" sx={{ color: ELIG_TOKENS.inkSoft, textTransform: 'none' }}>
      Cancel
    </Button>
  </Box>
);

const Field = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ mb: 1.5 }}>{children}</Box>
);

const Helper = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      color: ELIG_TOKENS.inkSoft,
      fontStyle: 'italic',
      bgcolor: ELIG_TOKENS.bg,
      p: 1,
      borderRadius: 0.5,
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

const formatCard = (
  card:
    | {
        card_brand?: string | null;
        card_last4?: string | null;
        is_default?: boolean;
      }
    | undefined,
): string => {
  if (!card) return '—';
  const brand = card.card_brand
    ? card.card_brand.charAt(0).toUpperCase() + card.card_brand.slice(1)
    : 'Card';
  const last4 = card.card_last4 ?? '••••';
  return `${brand} •••• ${last4}${card.is_default ? ' (default)' : ''}`;
};

const buildSelectedInvoiceReason = (
  groups: DisplayEncounterChargeGroup[],
  selectedIds: Set<string>,
): string => {
  const encounterParts = new Map<string, Map<string, { label: string; code: string | null; amountCents: number }>>();
  for (const group of groups) {
    const selectedLines = group.lines.filter(line => selectedIds.has(line.id));
    if (selectedLines.length === 0) continue;
    const encounterLabel = [
      group.encounterTypeLabel,
      group.serviceDate ? `on ${shortDate(group.serviceDate)}` : null,
    ].filter(Boolean).join(' ') || 'Visit';
    const lineParts = encounterParts.get(encounterLabel) ?? new Map<string, {
      label: string;
      code: string | null;
      amountCents: number;
    }>();
    for (const line of selectedLines) {
      const code = normalizeCode(line.code);
      const label = receiptLineLabel(line);
      const key = `${label}|${code ?? ''}`;
      const existing = lineParts.get(key);
      lineParts.set(key, {
        label,
        code,
        amountCents: (existing?.amountCents ?? 0) + line.amountCents,
      });
    }
    encounterParts.set(encounterLabel, lineParts);
  }
  return Array.from(encounterParts.entries())
    .map(([encounterLabel, lineParts]) => {
      const responsibilityLabel = Array.from(lineParts.values())
        .map(selectedLineReasonLabel)
        .join(' + ');
      return [encounterLabel, responsibilityLabel].filter(Boolean).join(' - ');
    })
    .join('; ');
};

const selectedLineReasonLabel = ({
  label,
  code,
  amountCents,
}: {
  label: string;
  code: string | null;
  amountCents: number;
}): string =>
  `${label} ${dollars(amountCents)}${code && label !== `CPT ${code}` ? ` (CPT ${code})` : ''}`;

const dollars = (cents: number): string => {
  const abs = Math.abs(cents);
  if (abs % 100 === 0) return `$${abs / 100}`;
  return `$${(abs / 100).toFixed(2)}`;
};

const shortDate = (date: string): string => {
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const normalizeCode = (code: string | null | undefined): string | null => {
  const normalized = code?.replace(/^CPT:/, '').replace(/^CPT\s+/, '').trim();
  return normalized || null;
};

const normalizeDescription = (
  description: string | null | undefined,
  code: string | null,
): string | null => {
  const normalized = description?.replace(/^CPT:/, '').replace(/^CPT\s+/, '').trim();
  if (!normalized || normalized === code) return null;
  return description ?? null;
};

const primaryFor = (method: SettleMethod): string => {
  switch (method) {
    case 'card_on_file':
      return 'Charge card';
    case 'invoice':
      return 'Send invoice';
    case 'remind_invoice':
      return 'Send reminder';
    case 'void_invoice':
      return 'Void invoice';
    case 'cash':
      return 'Record payment';
    case 'external':
      return 'Log collection';
    case 'write_off':
      return 'Write off';
  }
};
